import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@consigli/shadcn';
import { type BeamstarDayTotals } from '@consigli/types';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import { formatDay } from '../format';

export interface DailySeries {
  key: string;
  label: string;
  color: string;
  value: (day: BeamstarDayTotals) => number;
}

interface DailyChartProps {
  title: string;
  description: string;
  days: BeamstarDayTotals[];
  /** Stacked in this order, the first on the baseline. */
  series: DailySeries[];
  formatTick: (value: number) => string;
}

export const DailyChart = ({ title, description, days, series, formatTick }: DailyChartProps) => {
  const config: ChartConfig = Object.fromEntries(
    series.map(({ key, label, color }) => [key, { label, color }]),
  );
  const rows = days.map((day) => ({
    day: day.day,
    ...Object.fromEntries(series.map((item) => [item.key, item.value(day)])),
  }));

  return (
    <Card className="shadow-none">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={config} className="aspect-auto h-64 w-full">
          <BarChart data={rows} accessibilityLayer>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={24}
              tickFormatter={formatDay}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={48}
              allowDecimals={false}
              tickFormatter={formatTick}
            />
            <ChartTooltip
              content={<ChartTooltipContent labelFormatter={(day) => formatDay(String(day))} />}
            />
            <ChartLegend content={<ChartLegendContent />} />
            {series.map((item, index) => (
              <Bar
                key={item.key}
                dataKey={item.key}
                stackId="day"
                fill={`var(--color-${item.key})`}
                stroke="var(--card)"
                strokeWidth={2}
                maxBarSize={24}
                radius={index === series.length - 1 ? [4, 4, 0, 0] : 0}
              />
            ))}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
